import {
  useCohortDetail,
  useForecast,
  useForward,
  useNrr,
  useRenewalsLiveRefresh,
  useRetention,
} from "../api";
import { ActualRetention } from "../components/dashboard/ActualRetention";
import { CsmForecastSummary } from "../components/dashboard/CsmForecastSummary";
import { DataCadenceBanner } from "../components/dashboard/DataCadenceBanner";
import { ForecastMonth } from "../components/dashboard/ForecastMonth";
import { RenewalCohortDetail } from "../components/dashboard/RenewalCohortDetail";
import { TtmForward } from "../components/dashboard/TtmForward";
import { TtmRetention } from "../components/dashboard/TtmRetention";
import { RenewalsSubNav } from "../components/SubNav";

export function RenewalsDashboardPage() {
  // Bring the whole dashboard up in parallel; sections render as their slice
  // arrives. Each hook below already caches and dedupes; the SSE hook
  // invalidates the lot when an annotation changes in another tab.
  const nrr = useNrr();
  const retention = useRetention();
  const forecast = useForecast();
  const forward = useForward();
  const cohort = useCohortDetail();
  useRenewalsLiveRefresh();

  const anyLoading =
    nrr.isLoading || retention.isLoading || forecast.isLoading || forward.isLoading || cohort.isLoading;
  const anyError = nrr.error || retention.error || forecast.error || forward.error || cohort.error;

  const currentMonth = forecast.data?.current_month ?? null;
  const nextMonth = currentMonth ? shiftYm(currentMonth, 1) : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Renewals — Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Retention, forecast coverage, and forward-looking TTM NRR/GDR for the live renewal book.
        </p>
      </div>
      <RenewalsSubNav />

      {anyLoading && (
        <p className="text-sm text-muted-foreground">Loading dashboard…</p>
      )}
      {anyError && (
        <p className="text-sm text-destructive">{String(anyError)}</p>
      )}

      {forecast.data && forward.data && (
        <DataCadenceBanner
          currentMonth={forecast.data.current_month}
          latestClosed={forward.data.latest_closed_period}
        />
      )}

      {retention.data && <ActualRetention data={retention.data} />}
      {forecast.data && currentMonth && (
        <ForecastMonth data={forecast.data} month={currentMonth} label="Current month" />
      )}
      {forecast.data && nextMonth && (
        <ForecastMonth data={forecast.data} month={nextMonth} label="Next month" />
      )}
      {nrr.data && forward.data && (
        <TtmRetention data={nrr.data} forward={forward.data} />
      )}
      {forward.data && <TtmForward data={forward.data} />}
      {forecast.data && <CsmForecastSummary data={forecast.data} />}
      {cohort.data && <RenewalCohortDetail data={cohort.data} />}
    </div>
  );
}

function shiftYm(ym: string, months: number): string {
  const parts = ym.split("-");
  const y = Number(parts[0] ?? 0);
  const m = Number(parts[1] ?? 1);
  const total = y * 12 + (m - 1) + months;
  const yy = Math.floor(total / 12);
  const mm = total % 12;
  return `${String(yy).padStart(4, "0")}-${String(mm + 1).padStart(2, "0")}`;
}
